/**
 * Question Completeness Tracker Utility
 * 
 * Tracks wizard question answers and updates:
 * - Config item completeness percentage
 * - SOW overall progress
 * - Discovery grid status
 * - Gamification metrics
 */

import { projectId } from './supabase/info';

export interface QuestionAnswer {
  questionId: string;
  questionText: string;
  answer: string;
  answeredAt: string;
  answeredBy: string;
  confidence?: 'high' | 'medium' | 'low';
  source?: 'wizard' | 'discovery_session' | 'ai_assistant' | 'manual';
}

export interface CompletenessUpdate {
  configItemId: string;
  totalQuestions: number;
  answeredQuestions: number;
  completenessPercentage: number;
  updatedAt: string;
}

/**
 * Calculate completeness percentage based on answered questions
 */
export function calculateCompleteness(
  totalQuestions: number,
  answeredQuestions: number
): number {
  if (totalQuestions === 0) return 0;
  return Math.min(100, Math.round((answeredQuestions / totalQuestions) * 100));
}

/**
 * Save wizard answers and update config item completeness
 */
export async function saveWizardAnswers(
  configItemId: string,
  companyId: string,
  answers: QuestionAnswer[],
  accessToken: string
): Promise<CompletenessUpdate> {
  try {
    const response = await fetch(
      `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config-items/${companyId}/${configItemId}/wizard-answers`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ answers }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to save wizard answers');
    }

    return await response.json();
  } catch (error) {
    console.error('Error saving wizard answers:', error);
    throw error;
  }
}

/**
 * Get completeness status for a config item
 */
export async function getConfigItemCompleteness(
  configItemId: string,
  companyId: string,
  accessToken: string
): Promise<CompletenessUpdate> {
  try {
    const response = await fetch(
      `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config-items/${companyId}/${configItemId}/completeness`,
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get completeness');
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting completeness:', error);
    throw error;
  }
}

/**
 * Trigger SOW-wide completeness recalculation
 * This updates overall progress metrics and grid status
 */
export async function recalculateSOWCompleteness(
  sowId: string,
  accessToken: string
): Promise<{ progressPercentage: number; totalItems: number; completedItems: number }> {
  try {
    const response = await fetch(
      `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/sows/${sowId}/recalculate-completeness`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to recalculate SOW completeness');
    }

    return await response.json();
  } catch (error) {
    console.error('Error recalculating SOW completeness:', error);
    throw error;
  }
}

/**
 * Get all answered questions for a config item
 */
export async function getAnsweredQuestions(
  configItemId: string,
  companyId: string,
  accessToken: string
): Promise<QuestionAnswer[]> {
  try {
    const response = await fetch(
      `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config-items/${companyId}/${configItemId}/answers`,
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get answers');
    }

    const data = await response.json();
    return data.answers || [];
  } catch (error) {
    console.error('Error getting answered questions:', error);
    throw error;
  }
}

/**
 * Update a single question answer (for inline editing)
 */
export async function updateQuestionAnswer(
  configItemId: string,
  companyId: string,
  questionId: string,
  newAnswer: string,
  accessToken: string
): Promise<void> {
  try {
    const response = await fetch(
      `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config-items/${companyId}/${configItemId}/answers/${questionId}`,
      {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ answer: newAnswer }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to update answer');
    }
  } catch (error) {
    console.error('Error updating question answer:', error);
    throw error;
  }
}
